import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import Input from '../../../Input';
import Button from '../../../Button';
import Tooltip from '../../../Tooltip';

import {
  EditField as EditIcon,
  FieldApply as ApplyIcon,
  FieldClose as CloseIcon,
} from '@icons/common';

const ERRORS = {
  EMPTY: 'Поле не должно быть пустым.',
  PATTERN: 'Неправильный формат',
};

export interface EditableFieldProps {
  children?: React.ReactNode;
  className?: string;
  error?: string | null;
  label: string | null;
  name: string;
  placeholder: string;
  preventClose: boolean;
  readOnly: boolean;
  value: string | null;
  required?: boolean;
  validation?: { error: string; pattern: RegExp } | null;
  disabled?: boolean;

  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onOpen?: () => void;
  onReset: ({ name }: { name: EditableFieldProps['name'] }) => void;
  onSave: (data: {
    name: EditableFieldProps['name'];
    value: EditableFieldProps['value'];
  }) => void;
}

interface EditableFieldState {
  error: EditableFieldProps['error'];
  isEditing: boolean;
  prevValue: EditableFieldProps['value'];
}

class EditableField extends React.PureComponent<
  EditableFieldProps,
  EditableFieldState
> {
  static defaultProps = {
    children: null,
    className: '',
    error: null,
    label: null,
    name: '',
    onChange: Function.prototype,
    onFocus: Function.prototype,
    onOpen: Function.prototype,
    onReset: Function.prototype,
    onSave: Function.prototype,
    placeholder: '',
    preventClose: false,
    readOnly: false,
    required: false,
    validation: null,
    value: '',
  };

  constructor(props) {
    super(props);

    const { value } = this.props;

    this.state = {
      error: null,
      isEditing: false,
      prevValue: value,
    };

    this.handleFocus = this.handleFocus.bind(this);
    this.handleKeyPress = this.handleKeyPress.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.handleSave = this.handleSave.bind(this);
    this.inputRenderer = this.inputRenderer.bind(this);
    this.labelRenderer = this.labelRenderer.bind(this);
    this.toggleEditing = this.toggleEditing.bind(this);
  }

  handleFocus() {
    const { onFocus } = this.props;

    this.setState({ error: null }, onFocus);
  }

  handleKeyPress({ key }) {
    const { error } = this.state;
    if (error) {
      this.setState({ error: null });
    }

    if (key === 'Escape') {
      this.handleReset();
    }

    if (key === 'Enter') {
      this.handleSave();
    }
  }

  handleSave() {
    const { name, onSave, preventClose, required, validation, value } =
      this.props;

    if (required && !value) {
      this.setState({ error: ERRORS.EMPTY });
      return;
    }

    if (validation) {
      const pattern = _.get(validation, 'pattern') || null;
      const patternError = _.get(validation, 'error') || ERRORS.PATTERN;

      if (pattern && value) {
        const regex = new RegExp(pattern);
        const isValid = regex.test(value);

        if (!isValid) {
          this.setState({ error: patternError });
          return;
        }
      }
    }

    this.setState(
      {
        isEditing: preventClose,
        prevValue: value,
      },
      () => onSave({ name, value })
    );
  }

  handleOpen() {
    const { onOpen } = this.props;
    onOpen?.();
  }

  handleReset() {
    const { name, onReset } = this.props;
    this.setState(
      {
        error: null,
        isEditing: false,
      },
      () => onReset({ name })
    );
  }

  labelRenderer() {
    const { label, readOnly } = this.props;
    const { isEditing } = this.state;

    return (
      <StyledLabelWrap>
        <StyledLabel>{label}</StyledLabel>
        {!isEditing && !readOnly && (
          <StyledEditButton onClick={this.toggleEditing}>
            <EditIcon />
          </StyledEditButton>
        )}
      </StyledLabelWrap>
    );
  }

  inputRenderer() {
    const { prevValue, isEditing } = this.state;
    const {
      children,
      label,
      name,
      onChange,
      placeholder,
      readOnly,
      value,
      required,
      disabled,
    } = this.props;

    const isSavable =
      (!required || (required && !!value)) && value !== prevValue;

    return isEditing ? (
      <>
        <StyledInput
          autoFocus
          value={value}
          name={name}
          placeholder={placeholder}
          onFocus={this.handleFocus}
          onKeyDown={this.handleKeyPress}
          onChange={onChange}
        />
        {isSavable && (
          <StyledApplyButton onClick={this.handleSave}>
            <ApplyIcon />
          </StyledApplyButton>
        )}
        <StyledCloseButton onClick={this.handleReset}>
          <CloseIcon />
        </StyledCloseButton>
      </>
    ) : (
      <>
        {children ||
          (value ? (
            <StyledValue disabled={disabled} onClick={this.handleOpen}>
              {value}
            </StyledValue>
          ) : (
            <StyledPlaceholder>{placeholder}</StyledPlaceholder>
          ))}
        {!label && !readOnly && (
          <StyledEditButton onClick={this.toggleEditing}>
            <EditIcon />
          </StyledEditButton>
        )}
      </>
    );
  }

  toggleEditing() {
    this.setState(prevState => ({
      isEditing: !prevState.isEditing,
    }));
  }

  render() {
    const { label, error: externalError, className } = this.props;
    const { error: localError } = this.state;
    const error = externalError || localError;

    return (
      <StyledEditableField className={className}>
        {label && this.labelRenderer()}
        <StyledInputWrap>
          {error && (
            <StyledTooltipWrap>
              <StyledTooltip shown content={error} />
            </StyledTooltipWrap>
          )}
          {this.inputRenderer()}
        </StyledInputWrap>
      </StyledEditableField>
    );
  }
}

const StyledFlex = styled.div`
  display: flex;
  align-items: center;
`;

const StyledLabelWrap = styled(StyledFlex)`
  width: 100px;
  min-width: 100px;
`;

const StyledInputWrap = styled(StyledFlex)`
  align-items: center;
  display: flex;
  flex: 1;
  position: relative;
`;

const StyledTooltip = styled(Tooltip)`
  border: none;
  width: 220px;
  font-size: 14px;
  text-align: center;
  margin-bottom: -15px;
  background-color: #f8e71c;
  margin-left: -110px;

  :before {
    border-top-color: #f8e71c;
  }
  :after {
    border-top-color: #f8e71c;
  }
`;

const StyledButtonReset = styled(Button)`
  padding: 0;
  width: auto;
  border: none;
  background: none;
  line-height: normal;
`;

const StyledLabel = styled.div`
  font-size: 14px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: calc(100% - 25px);
`;

export const StyledPlaceholder = styled.div`
  color: #919499;
  font-size: 14px;
  cursor: default;
  display: inline-block;
`;

const StyledEditButton = styled(StyledButtonReset)`
  width: 16px;
  height: 15px;
  margin-top: 2px;
  margin-left: 6px;
  opacity: 0;

  &:hover svg path {
    fill: #007aff;
  }
`;

const StyledTooltipWrap = styled.div`
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  position: absolute;
`;

export const StyledValue = styled(StyledButtonReset)`
  height: auto;
  color: #007aff;
  font-size: 14px;
  overflow: hidden;
  text-align: left;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-decoration: underline;

  ${props =>
    props.disabled &&
    `
    color: #c2c5cc;
  `}
`;

export const StyledInput = styled(Input)`
  background: none;
  border: none;
  flex: 1;
  font-family: inherit;
  font-size: 14px;
  font-weight: normal;
  padding: 0;
`;

const StyledApplyButton = styled(StyledButtonReset)`
  width: 15px;
  height: 12px;
  margin-left: 10px;
`;

const StyledCloseButton = styled(StyledButtonReset)`
  width: 12px;
  height: 12px;
  margin-left: 10px;
`;

const StyledEditableField = styled(StyledFlex)`
  height: 36px;

  :hover {
    ${StyledEditButton} {
      opacity: 1;
    }
  }
`;

export default EditableField;
